import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import Loader from 'components/Loader/Loader';
import { replace } from 'connected-react-router';
import { getOrganizationAction, updateIsDemoModeAction } from 'actions/actions';
import DashboardContent from 'components/Dashboard/DashboardContent/DashboardContent';
import PayoutsModuleContainer from 'containers/PayoutsModuleContainer';
import Sidebar from 'components/Sidebar/Sidebar';
import SpotsRoutes from 'containers/SpotsRoutes';
import TeamModuleContainer from 'containers/TeamModuleContainer';
import { AppState } from 'reducers/reducers';
import { selectedOrganizationId } from 'selectors';
import IncomeStatementContainer from 'containers/IncomeStatementContainer';
import { DocumentsProvider } from 'contexts/DocumentsContext';
import DocumentsModuleContainer from 'containers/DocumentsModuleContainer';

type OrganizationViewProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const OrganizationView = ({
    organization,
    selectedOrganizationId: orgId,
    getOrganization,
    replace: connectedReplace,
    setDemoMode,
}: OrganizationViewProps): JSX.Element => {
    const { isExact } = useRouteMatch(`/organization/${orgId}`) || { isExact: false };
    const { pathname, search } = useLocation();

    useEffect(() => {
        if (isExact) {
            // want to redirect to spots
            connectedReplace(`/organization/${orgId}/spots`);
        }
    }, [isExact, connectedReplace, orgId]);

    // A demo opened from the launcher with demo mode on carries `?demoMode=true`.
    // Apply the blur, then strip the param so it doesn't stick around.
    useEffect(() => {
        const params = new URLSearchParams(search);
        if (params.get('demoMode') !== 'true') return;
        setDemoMode(true);
        params.delete('demoMode');
        const rest = params.toString();
        connectedReplace(`${pathname}${rest ? `?${rest}` : ''}`);
    }, [search, pathname, setDemoMode, connectedReplace]);

    useEffect(() => {
        getOrganization(orgId);
    }, [orgId, getOrganization]);

    return (
        <Sidebar>
            {organization === null ? (
                <Loader centered />
            ) : (
                <Switch>
                    <Route path={`/organization/${orgId}/team`}>
                        <DashboardContent title="Team">
                            <TeamModuleContainer />
                        </DashboardContent>
                    </Route>
                    <Route path={`/organization/${orgId}/payouts`}>
                        <DashboardContent title="Payouts">
                            <PayoutsModuleContainer />
                        </DashboardContent>
                    </Route>
                    <Route path={`/organization/${orgId}/income-statement`}>
                        <DashboardContent title="Income Statement">
                            <IncomeStatementContainer />
                        </DashboardContent>
                    </Route>
                    <Route path={`/organization/${orgId}/documents`}>
                        <DocumentsProvider>
                            <DocumentsModuleContainer />
                        </DocumentsProvider>
                    </Route>
                    <DocumentsProvider>
                        <SpotsRoutes />
                    </DocumentsProvider>
                </Switch>
            )}
        </Sidebar>
    );
};

function mapStateToProps(state: AppState) {
    return {
        organization: state.organization.organization,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        selectedOrganizationId: selectedOrganizationId(state)!,
    };
}

const mapDispatchToProps = {
    getOrganization: getOrganizationAction,
    replace,
    setDemoMode: updateIsDemoModeAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationView);
